import { useEffect, useMemo, useState } from 'react';
import type { ProductionOrder, StopReason } from '@ecinsa/shared';
import { Dialog, Empty, Pill } from './ui';
import { Numpad, PinDisplay } from './Numpad';
import { qty, shortDate } from '../lib/format';

/** Rejilla de causas de parada: en planta se elige de un golpe, no de una lista. */
export function ReasonPicker({
  title,
  subtitle,
  reasons,
  optional,
  onPick,
  onClose,
}: {
  title: string;
  subtitle?: string;
  reasons: StopReason[];
  optional: boolean;
  onPick: (code: string | null) => void;
  onClose: () => void;
}) {
  return (
    <Dialog title={title} subtitle={subtitle} onClose={onClose} wide>
      <div className="chips" style={{ marginBottom: 20 }}>
        {reasons.map((reason) => (
          <button key={reason.code} className="chip" onClick={() => onPick(reason.code)}>
            {reason.name}
          </button>
        ))}
        {reasons.length === 0 ? <p className="muted">No hay causas configuradas.</p> : null}
      </div>
      <div className="row">
        <button className="btn" onClick={onClose}>
          Cancelar
        </button>
        <div className="spacer" />
        {optional ? (
          <button className="btn btn--primary btn--lg" onClick={() => onPick(null)}>
            Sin causa · continuar
          </button>
        ) : null}
      </div>
    </Dialog>
  );
}

/** Autorización puntual de un jefe de producción sin cerrar la sesión actual. */
export function SupervisorPin({ action, onConfirm, onClose }: { action: string; onConfirm: (pin: string) => void; onClose: () => void }) {
  const [pin, setPin] = useState('');
  return (
    <Dialog title="Autorización de jefe de producción" subtitle={action} onClose={onClose}>
      <div className="stack">
        <PinDisplay length={pin.length} placeholder="PIN del jefe de producción" />
        <Numpad value={pin} onChange={setPin} onSubmit={() => pin && onConfirm(pin)} submitLabel="Autorizar" />
        <button className="btn" onClick={onClose}>
          Cancelar
        </button>
      </div>
    </Dialog>
  );
}

/**
 * Selección de OF.
 *
 * Con `manualEntry` (parámetro SELECMAN del terminal clásico) el campo queda
 * enfocado para leer con pistola de códigos; si no, se muestra la lista con
 * buscador. Ambas vías conviven: el buscador acepta igualmente un escaneo.
 */
export function OrderPicker({
  orders,
  manualEntry,
  onPick,
}: {
  orders: ProductionOrder[];
  manualEntry: boolean;
  onPick: (order: ProductionOrder) => void;
}) {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return orders;
    return orders.filter(
      (o) =>
        o.docNum.toLowerCase().includes(needle) ||
        o.itemCode.toLowerCase().includes(needle) ||
        o.itemDescription.toLowerCase().includes(needle) ||
        (o.project ?? '').toLowerCase().includes(needle),
    );
  }, [orders, search]);

  // Un escaneo que coincide con una sola OF entra directo: es el gesto más
  // frecuente y ahorra una pulsación por cada imputación.
  useEffect(() => {
    if (!manualEntry) return;
    const exact = orders.find((o) => o.docNum === search.trim());
    if (exact) onPick(exact);
  }, [search, manualEntry, orders, onPick]);

  return (
    <div className="stack">
      <input
        className="input"
        autoFocus
        placeholder="Escanea o escribe nº de OF, artículo o expediente"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <div className="list">
        {filtered.map((order) => (
          <button key={order.docEntry} className="list-item" onClick={() => onPick(order)}>
            <div className="list-item__main">
              <div className="list-item__title">
                OF {order.docNum} · {order.itemDescription}
              </div>
              <div className="list-item__sub">
                {order.itemCode} · {qty(order.completedQuantity)}/{qty(order.plannedQuantity)} ud
                {order.project ? ` · ${order.project}` : ''}
                {order.customerName ? ` · ${order.customerName}` : ''}
              </div>
            </div>
            <Pill tone={order.dueDate && new Date(`${order.dueDate}T23:59`) < new Date() ? 'danger' : 'default'}>
              Entrega {shortDate(order.dueDate)}
            </Pill>
          </button>
        ))}
        {filtered.length === 0 ? <Empty icon="🔍" title="Ninguna OF coincide" body="Revisa el número o borra el filtro." /> : null}
      </div>
    </div>
  );
}

export function PhasePicker({
  phases,
  names,
  onPick,
}: {
  phases: { code: string; enabled: boolean; reason: string | null }[];
  names: Map<string, string>;
  onPick: (code: string) => void;
}) {
  if (phases.length === 0) {
    return <Empty icon="🧩" title="La OF no tiene fases" body="Revisa la lista de materiales: las fases se definen en sus líneas." />;
  }
  return (
    <div className="list">
      {phases.map((phase) => (
        <button key={phase.code} className="list-item" disabled={!phase.enabled} onClick={() => onPick(phase.code)}>
          <div className="list-item__main">
            <div className="list-item__title">{names.get(phase.code) ?? phase.code}</div>
            <div className="list-item__sub">{phase.reason ?? `Fase ${phase.code}`}</div>
          </div>
          {phase.enabled ? <Pill tone="accent">Disponible</Pill> : <Pill>Bloqueada</Pill>}
        </button>
      ))}
    </div>
  );
}

/** Introducción de cantidades con teclado grande y decimales. */
export function QuantityPad({ value, onChange, unit }: { value: string; onChange: (v: string) => void; unit?: string | null }) {
  const press = (key: string) => {
    if (key === 'C') return onChange('');
    if (key === '⌫') return onChange(value.slice(0, -1));
    if (key === ',' && value.includes(',')) return;
    onChange(`${value}${key}`.slice(0, 12));
  };
  return (
    <div className="stack">
      <div className="input input--qty" style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10 }}>
        {value || '0'} <span className="muted small">{unit ?? ''}</span>
      </div>
      <div className="numpad">
        {['1', '2', '3', '4', '5', '6', '7', '8', '9', ',', '0', '⌫'].map((key) => (
          <button key={key} className="numpad__key" onClick={() => press(key)}>
            {key}
          </button>
        ))}
      </div>
    </div>
  );
}

export function parseQty(value: string): number {
  const parsed = Number.parseFloat(value.replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : 0;
}
